// The Order: a page of the Codex where the player sets each glyph they have
// seen in its one true place. The places are the same cube as the map: a
// line for one-ring glyphs, a tablet for two rings, three tablets for three.
// The inner ring runs left to right, the next ring bottom to top, the outer
// ring picks the tablet. Three placed in a straight line light the line.

import { err, ok, type Result } from "./result.ts"
import { fullDeck, keyOf, ringAt, ringIndexes, ringsOf, scaleOf, WIDTHS, withRing, type GlyphKey, type RingIndex, type Rings, type Width } from "./rings.ts"
import { keysOf, placeGlyph, type Progress } from "./progress.ts"

export type OrderLine = { readonly axis: RingIndex; readonly pts: readonly [GlyphKey, GlyphKey, GlyphKey] }
export type Stage = { readonly w: Width; readonly points: readonly GlyphKey[]; readonly lines: readonly OrderLine[] }

const lineFrom = (base: Rings, axis: RingIndex): OrderLine => ({
  axis,
  pts: [keyOf(withRing(base, axis, -1)), keyOf(withRing(base, axis, 0)), keyOf(withRing(base, axis, 1))],
})

const startsLine = (base: Rings) => (axis: RingIndex): boolean => ringAt(base, axis) === -1
const linesFrom = (base: Rings): readonly OrderLine[] => ringIndexes(base).filter(startsLine(base)).map((axis) => lineFrom(base, axis))

const stageOf = (w: Width): Stage => {
  const deck = fullDeck(w)
  return { w, points: deck.map(keyOf), lines: deck.flatMap(linesFrom) }
}

export const STAGES: readonly Stage[] = WIDTHS.map(stageOf)
export const ORDER_SLOTS = STAGES.reduce((n, s) => n + s.points.length, 0)

// Where a point sits inside its tablet: column (inner ring) and row (next).
export const slotColumn = (r: Rings): number => (r[0] === "folded" ? 0 : r[0]) + 1
export const slotRow = (r: Rings): number => {
  const y = ringAt(r, 1)
  return y === undefined || y === "folded" ? 0 : 1 - y
}

const isUnfolded = (k: GlyphKey): boolean => scaleOf(ringsOf(k)) === 1

// Unfolded glyphs the player has seen, of one width.
export const seenOfWidth = (progress: Progress, w: Width): readonly GlyphKey[] =>
  keysOf(progress.seen).filter((k) => isUnfolded(k) && k.length === w)

// The stages the player has met: a stage shows once a glyph of its width has been seen.
export const stagesMet = (progress: Progress): readonly Stage[] => STAGES.filter((s) => seenOfWidth(progress, s.w).length > 0)

export const isPlaced = (progress: Progress, k: GlyphKey): boolean => progress.order[k] === true

export type OrderCount = { readonly done: number; readonly total: number }

export const orderCount = (progress: Progress): OrderCount => {
  const stages = stagesMet(progress)
  return {
    done: stages.reduce((n, s) => n + s.points.filter((p) => isPlaced(progress, p)).length, 0),
    total: stages.reduce((n, s) => n + s.points.length, 0),
  }
}

export const stageWhole = (progress: Progress, s: Stage): boolean => s.points.every((p) => isPlaced(progress, p))
export const orderWhole = (progress: Progress): boolean => STAGES.every((s) => stageWhole(progress, s))
export const litOrderLines = (progress: Progress, s: Stage): readonly OrderLine[] =>
  s.lines.filter((l) => l.pts.every((p) => isPlaced(progress, p)))

export type Placed = { readonly progress: Progress; readonly lines: readonly OrderLine[]; readonly stageWhole: boolean }
export type Misplaced = { readonly faults: readonly RingIndex[] }

const faultsBetween = (a: Rings, b: Rings): readonly RingIndex[] =>
  a.length === b.length ? ringIndexes(a).filter((i) => ringAt(a, i) !== ringAt(b, i)) : ringIndexes(a)

// Set a glyph in a slot. Right: it locks, maybe lighting lines. Wrong: the
// rings that do not belong there.
export const place = (progress: Progress, key: GlyphKey, slot: GlyphKey): Result<Misplaced, Placed> => {
  if (key !== slot) return err({ faults: faultsBetween(ringsOf(key), ringsOf(slot)) })
  const next = placeGlyph(progress, key)
  const stage = STAGES.find((s) => s.points.includes(key))
  if (stage === undefined) return ok({ progress: next, lines: [], stageWhole: false })
  const lines = litOrderLines(next, stage).filter((l) => l.pts.includes(key))
  return ok({ progress: next, lines, stageWhole: stageWhole(next, stage) })
}
